// src/components/groupAdmin/OrdersTab.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

function currencyFmtMinor(amountMinor, currency = 'USD') {
  const amt = Number(amountMinor || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amt);
  } catch {
    return `${amt.toFixed(2)} ${currency}`;
  }
}

function currencyFmtMajor(amountMajor, currency = 'USD') {
  const amt = Number(amountMajor || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amt);
  } catch {
    return `${amt.toFixed(2)} ${currency}`;
  }
}

function sumBalanceBuckets(buckets, currencyUpper) {
  const c = String(currencyUpper || 'USD').toLowerCase();
  const row = (buckets || []).find((x) => String(x.currency || '').toLowerCase() === c);
  return row ? Number(row.amount || 0) : 0; // minor units
}

export default function OrdersTab({ groupId }) {
  const { t } = useTranslation();

  const [orders, setOrders] = useState([]);
  const [group, setGroup] = useState(null);

  // Stripe payouts + balance
  const [payoutsData, setPayoutsData] = useState(null);

  // Stable "now" snapshot to keep render pure
  const [nowMs, setNowMs] = useState(null);

  const fetchAll = useCallback(
    async (signal) => {
      try {
        const [g, o, p] = await Promise.all([
          api.get(`/groups/${groupId}`),
          api.get(`/groups/${groupId}/orders`),
          api.get(`/connect/groups/${groupId}/payouts`),
        ]);
        if (signal?.aborted) return;

        setGroup(g.data || null);
        setOrders(o.data || []);
        setPayoutsData(p.data || null);
        setNowMs(Date.now());
      } catch (e) {
        if (!signal?.aborted) {
          toast.error(e?.response?.data?.detail || t('groupAdminTabs.orders.toastLoadError'));
        }
      }
    },
    [groupId, t]
  );

  useEffect(() => {
    const controller = new AbortController();
    const id = setTimeout(() => fetchAll(controller.signal), 0);
    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [fetchAll]);

  const currency = (group?.currency || 'USD').toUpperCase();

  // Balance summary (minor units)
  const balanceAvailableMinor = useMemo(() => {
    return sumBalanceBuckets(payoutsData?.balance?.available, currency);
  }, [payoutsData, currency]);

  const balancePendingMinor = useMemo(() => {
    return sumBalanceBuckets(payoutsData?.balance?.pending, currency);
  }, [payoutsData, currency]);

  // Real payouts list -> render as "withdrawals"
  const payoutTx = useMemo(() => {
    const rows = (payoutsData?.payouts || []).map((p) => ({
      id: p.id,
      type: 'payout',
      title: 'Withdrawal (Stripe payout)',
      status: p.status || 'unknown',
      amount_minor: p.amount || 0,
      currency: String(p.currency || currency).toUpperCase(),
      created_at: p.created ? new Date(p.created * 1000).toISOString() : null,
      arrival_date: p.arrival_date ? new Date(p.arrival_date * 1000).toISOString() : null,
    }));

    // newest first
    return rows.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [payoutsData, currency]);

  const orderTx = useMemo(() => {
    return (orders || []).map((o) => ({
      id: o.id,
      type: 'order',
      title: `Order #${String(o.id).slice(0, 8)}`,
      status: o.status || 'pending',
      amount_major: Number(o.total_amount || 0),
      currency: (o.currency || currency).toUpperCase(),
      created_at: o.created_at || null,
    }));
  }, [orders, currency]);

  const last7DaysTotalMajor = useMemo(() => {
    if (!nowMs) return 0;
    const weekAgo = nowMs - 7 * 24 * 60 * 60 * 1000;
    return (orders || [])
      .filter((o) => (o.status || '').toLowerCase() === 'completed')
      .filter((o) => {
        const ts = o.created_at ? new Date(o.created_at).getTime() : 0;
        return ts >= weekAgo && ts <= nowMs;
      })
      .reduce((sum, o) => sum + Number(o.total_amount || 0), 0);
  }, [orders, nowMs]);

  // Combined transactions (payouts on top, then orders)
  const mergedTransactions = useMemo(() => {
    return [...payoutTx, ...orderTx];
  }, [payoutTx, orderTx]);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">{t('groupAdminTabs.orders.title')}</h2>
          <p className="text-sm text-slate-500 mt-1">Orders + withdrawals (Stripe payouts).</p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Last 7 days revenue</p>
            <p className="text-lg font-semibold">{currencyFmtMajor(last7DaysTotalMajor, currency)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Balance available</p>
            <p className="text-lg font-semibold">{currencyFmtMinor(balanceAvailableMinor, currency)}</p>
            <p className="text-xs text-slate-500 mt-1">Ready to pay out</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Balance pending</p>
            <p className="text-lg font-semibold">{currencyFmtMinor(balancePendingMinor, currency)}</p>
            <p className="text-xs text-slate-500 mt-1">Clearing / processing</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Stripe connected</p>
            <p className="text-lg font-semibold">
              {(group?.stripe_connect_status || 'not_started') === 'complete' ? 'Yes' : 'No'}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {payoutsData?.stripe_account_id ? `Acct: ${String(payoutsData.stripe_account_id).slice(0, 10)}…` : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Transactions */}
      {mergedTransactions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            {t('groupAdminTabs.orders.none')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mergedTransactions.map((tx) => (
            <Card key={tx.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  <span className="truncate">{tx.title}</span>
                  <Badge variant={tx.type === 'payout' ? 'default' : 'outline'}>
                    {tx.status}
                  </Badge>
                </CardTitle>

                <CardDescription>
                  {tx.created_at ? format(new Date(tx.created_at), 'PPP p') : ''}
                  {tx.type === 'payout' && tx.arrival_date ? (
                    <span className="block text-xs text-slate-500 mt-1">
                      Expected arrival: {format(new Date(tx.arrival_date), 'PPP')}
                    </span>
                  ) : null}
                </CardDescription>
              </CardHeader>

              <CardContent className="text-sm text-slate-600 space-y-2">
                {tx.type === 'payout' ? (
                  <p>
                    <strong>Amount:</strong> {currencyFmtMinor(tx.amount_minor, tx.currency)}
                  </p>
                ) : (
                  <p>
                    <strong>Amount:</strong> {currencyFmtMajor(tx.amount_major, tx.currency)}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
